/**
 * Study Group List Component
 * 
 * Displays hierarchically grouped studies by symbol and timeframe.
 * Supports collapsible groups and study selection for comparison.
 */

import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowDownAZ,
  ArrowUpAZ,
  ChevronDown,
  ChevronRight,
  Clock,
  Layers,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";

import { formatStrictWithApprox, parseApproxConfigFromToml } from "@/components/results/utils";
import { Badge } from "@/components/ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import type { Study } from "@/types/api";
import { cn } from "@/utils/cn";
import {
  type StudyWithMetadata,
  type SymbolGroup,
  type TimeframeGroup,
  getFreshnessColorClass,
  groupParallelStudies,
  parseStudyTimestamp,
} from "@/utils/studyGrouping";

import { ParallelStudyCard } from "./ParallelStudyCard";
import { StudyCard } from "./StudyCard";

// =============================================================================
// Sort Types & Constants
// =============================================================================

type SortField = "sortino" | "win_rate" | "sharpe" | "max_dd" | "profit_factor" | "date";
type SortDirection = "asc" | "desc";

interface SortOption {
  field: SortField;
  direction: SortDirection;
}

const SORT_OPTIONS: { field: SortField; label: string; defaultDir: SortDirection }[] = [
  { field: "sortino", label: "Sortino", defaultDir: "desc" },
  { field: "win_rate", label: "Win Rate", defaultDir: "desc" },
  { field: "sharpe", label: "Sharpe", defaultDir: "desc" },
  { field: "max_dd", label: "Max DD", defaultDir: "asc" }, // Lower is better
  { field: "profit_factor", label: "Profit Factor", defaultDir: "desc" },
  { field: "date", label: "Date", defaultDir: "desc" },
];

/**
 * Get the sort value for a study based on the sort field.
 */
function getStudySortValue(study: Study, field: SortField): number | null {
  const toFiniteNumber = (value?: number | null) =>
    Number.isFinite(value) ? value : null;

  switch (field) {
    case "sortino":
      return toFiniteNumber(study.best_value);
    case "win_rate":
      return toFiniteNumber(study.win_rate);
    case "sharpe":
      return toFiniteNumber(study.sharpe_ratio);
    case "max_dd":
      return toFiniteNumber(study.max_drawdown);
    case "profit_factor":
      return toFiniteNumber(study.profit_factor);
    case "date":
      return parseStudyTimestamp(study.created_at);
    default:
      return null;
  }
}

/**
 * Sort studies by a given field and direction.
 */
function sortStudies(
  studies: Study[],
  sort: SortOption
): Study[] {
  return [...studies].sort((a, b) => {
    const aVal = getStudySortValue(a, sort.field);
    const bVal = getStudySortValue(b, sort.field);

    // Handle nulls - push them to the end
    if (aVal === null && bVal === null) return 0;
    if (aVal === null) return 1;
    if (bVal === null) return -1;

    // Sort based on direction
    const diff = aVal - bVal;
    return sort.direction === "asc" ? diff : -diff;
  });
}

// =============================================================================
// Types
// =============================================================================

interface StudyGroupListProps {
  groups: SymbolGroup[];
  selectedStudyIds: string[];
  onStudyClick: (studyId: string) => void;
  onStudySelect: (study: StudyWithMetadata) => void;
  onCompareAll?: (studies: Study[]) => void;
}

// =============================================================================
// Main Component
// =============================================================================

export function StudyGroupList({
  groups,
  selectedStudyIds,
  onStudyClick,
  onStudySelect,
  onCompareAll,
}: StudyGroupListProps) {
  // Track expanded state for each group
  const [expandedSymbols, setExpandedSymbols] = useState<Set<string>>(
    new Set(groups.map((g) => g.symbol)) // Expand all by default
  );
  const [expandedTimeframes, setExpandedTimeframes] = useState<Set<string>>(
    new Set(
      groups.flatMap((g) =>
        g.timeframes.map((tf) => `${g.symbol}:${tf.timeframe}`)
      )
    )
  );

  // Clean up stale entries when groups change (e.g., when filters are applied)
  useEffect(() => {
    const validSymbols = new Set(groups.map((g) => g.symbol));
    const validTimeframeKeys = new Set(
      groups.flatMap((g) =>
        g.timeframes.map((tf) => `${g.symbol}:${tf.timeframe}`)
      )
    );

    // Clean up stale symbols
    setExpandedSymbols((prev) => {
      const filtered = new Set([...prev].filter((s) => validSymbols.has(s)));
      // Add any new symbols that appeared
      for (const symbol of validSymbols) {
        filtered.add(symbol);
      }
      return filtered;
    });

    // Clean up stale timeframe keys
    setExpandedTimeframes((prev) => {
      const filtered = new Set([...prev].filter((k) => validTimeframeKeys.has(k)));
      // Add any new timeframe keys that appeared
      for (const key of validTimeframeKeys) {
        filtered.add(key);
      }
      return filtered;
    });
  }, [groups]);

  const toggleSymbol = (symbol: string) => {
    setExpandedSymbols((prev) => {
      const next = new Set(prev);
      if (next.has(symbol)) {
        next.delete(symbol);
      } else {
        next.add(symbol);
      }
      return next;
    });
  };

  const toggleTimeframe = (symbol: string, timeframe: string) => {
    const key = `${symbol}:${timeframe}`;
    setExpandedTimeframes((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  return (
    <div className="space-y-6 pb-20">
      {groups.map((symbolGroup) => (
        <SymbolGroupCard
          key={symbolGroup.symbol}
          group={symbolGroup}
          isExpanded={expandedSymbols.has(symbolGroup.symbol)}
          expandedTimeframes={expandedTimeframes}
          selectedStudyIds={selectedStudyIds}
          onToggleExpand={() => toggleSymbol(symbolGroup.symbol)}
          onToggleTimeframe={(tf) =>
            toggleTimeframe(symbolGroup.symbol, tf)
          }
          onStudyClick={onStudyClick}
          onStudySelect={onStudySelect}
          onCompareAll={onCompareAll}
        />
      ))}
    </div>
  );
}

// =============================================================================
// Symbol Group Card
// =============================================================================

interface SymbolGroupCardProps {
  group: SymbolGroup;
  isExpanded: boolean;
  expandedTimeframes: Set<string>;
  selectedStudyIds: string[];
  onToggleExpand: () => void;
  onToggleTimeframe: (timeframe: string) => void;
  onStudyClick: (studyId: string) => void;
  onStudySelect: (study: StudyWithMetadata) => void;
  onCompareAll: ((studies: Study[]) => void) | undefined;
}

function SymbolGroupCard({
  group,
  isExpanded,
  expandedTimeframes,
  selectedStudyIds,
  onToggleExpand,
  onToggleTimeframe,
  onStudyClick,
  onStudySelect,
  onCompareAll,
}: SymbolGroupCardProps) {
  const freshnessClass = getFreshnessColorClass(group.worstFreshness);

  return (
    <div className="bg-dark-900/30 rounded-lg border border-dark-800 overflow-hidden">
      {/* Symbol header */}
      <button
        onClick={onToggleExpand}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-dark-800/50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="flex items-center justify-center w-10 h-10 rounded-xl bg-gradient-to-br from-brand-blue/20 to-brand-cyan/20 border border-brand-blue/30 shadow-inner">
            <Layers className="h-5 w-5 text-brand-blue" />
          </div>
          <div className="text-left py-1">
            <h3 className="font-bold text-lg text-white tracking-tight">{group.displaySymbol}</h3>
            <p className="text-xs text-dark-500">
              {group.totalStudies} {group.totalStudies === 1 ? "study" : "studies"} •{" "}
              {group.timeframes.length} {group.timeframes.length === 1 ? "timeframe" : "timeframes"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge
            variant="outline"
            className={cn("text-xs border", freshnessClass)}
          >
            {group.worstFreshness}
          </Badge>
          <div className={cn(
            "p-1 rounded-full transition-colors",
            isExpanded ? "bg-dark-700/50 text-white" : "text-dark-500"
          )}>
            {isExpanded ? (
              <ChevronDown className="h-5 w-5" />
            ) : (
              <ChevronRight className="h-5 w-5" />
            )}
          </div>
        </div>
      </button>

      {/* Timeframe groups */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
          >
            <div className="border-t border-dark-800">
              {group.timeframes.map((tfGroup) => (
                <TimeframeGroupSection
                  key={tfGroup.timeframe}
                  group={tfGroup}
                  isExpanded={expandedTimeframes.has(
                    `${group.symbol}:${tfGroup.timeframe}`
                  )}
                  selectedStudyIds={selectedStudyIds}
                  onToggleExpand={() => onToggleTimeframe(tfGroup.timeframe)}
                  onStudyClick={onStudyClick}
                  onStudySelect={onStudySelect}
                  onCompareAll={onCompareAll}
                />
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

// =============================================================================
// Timeframe Group Section
// =============================================================================

interface TimeframeGroupSectionProps {
  group: TimeframeGroup;
  isExpanded: boolean;
  selectedStudyIds: string[];
  onToggleExpand: () => void;
  onStudyClick: (studyId: string) => void;
  onStudySelect: (study: StudyWithMetadata) => void;
  onCompareAll: ((studies: Study[]) => void) | undefined;
}

function TimeframeGroupSection({
  group,
  isExpanded,
  selectedStudyIds,
  onToggleExpand,
  onStudyClick,
  onStudySelect,
  onCompareAll,
}: TimeframeGroupSectionProps) {
  const freshnessClass = getFreshnessColorClass(group.worstFreshness);

  // Sort state - default to sortino descending
  const [sortOption, setSortOption] = useState<SortOption>({ field: "sortino", direction: "desc" });

  // Sort studies based on current sort option
  const sortedStudies = useMemo(() => {
    return sortStudies(group.studies, sortOption);
  }, [group.studies, sortOption]);

  // Group sorted studies for parallel study rendering
  const { parallelGroups, standalone } = useMemo(() => {
    return groupParallelStudies(sortedStudies);
  }, [sortedStudies]);

  // Create a unified list of items (parallel groups + standalone) sorted by the selected metric.
  // This ensures proper intermixing - parallel groups and standalone studies are sorted
  // together based on their metric values, not their type (parallel vs standalone).
  type DisplayItem = 
    | { type: "parallel"; group: typeof parallelGroups[number]; sortKey: number }
    | { type: "standalone"; study: typeof standalone[number]; sortKey: number };

  const sortedDisplayItems = useMemo(() => {
    const items: DisplayItem[] = [];

    // Helper to get the sort value for a parallel group based on the sort field
    const getGroupSortValue = (pg: typeof parallelGroups[number]): number | null => {
      // For date sorting, use the earliest valid created_at in the group.
      // This represents the group creation time and keeps parallel groups
      // interleaved correctly with standalone studies.
      if (sortOption.field === "date") {
        const times = pg.studies
          .map((study) => parseStudyTimestamp(study.created_at))
          .filter((time): time is number => time !== null);
        if (times.length === 0) return null;
        return Math.min(...times);
      }
      
      const values = pg.studies
        .map((s) => getStudySortValue(s, sortOption.field))
        .filter((v): v is number => v !== null);
      
      if (values.length === 0) return null;
      
      // For max_dd (lower is better), use minimum; for metrics (higher is better), use maximum
      return sortOption.field === "max_dd" ? Math.min(...values) : Math.max(...values);
    };

    // Add parallel groups with their sort key based on the current sort field
    for (const pg of parallelGroups) {
      const sortValue = getGroupSortValue(pg);
      // Use a fallback for null values: push to end by using -Infinity for desc, Infinity for asc
      const sortKey = Number.isFinite(sortValue)
        ? sortValue
        : sortOption.direction === "desc"
          ? -Infinity
          : Infinity;
      items.push({ type: "parallel", group: pg, sortKey });
    }

    // Add standalone studies with their sort key based on the current sort field
    for (const study of standalone) {
      const sortValue = getStudySortValue(study, sortOption.field);
      const sortKey = Number.isFinite(sortValue)
        ? sortValue
        : sortOption.direction === "desc"
          ? -Infinity
          : Infinity;
      items.push({ type: "standalone", study, sortKey });
    }

    // Sort based on the current sort option
    // For desc: higher values first (b - a), for asc: lower values first (a - b)
    items.sort((a, b) =>
      sortOption.direction === "desc" ? b.sortKey - a.sortKey : a.sortKey - b.sortKey
    );

    return items;
  }, [parallelGroups, standalone, sortOption]);

  // Convert selectedStudyIds array to Set for ParallelStudyCard
  const selectedStudyIdSet = new Set(selectedStudyIds);

  // Get current sort label
  const currentSortLabel = SORT_OPTIONS.find((opt) => opt.field === sortOption.field)?.label ?? "Sort";

  // Toggle sort direction or set new field
  const handleSortSelect = (field: SortField) => {
    if (field === sortOption.field) {
      // Toggle direction
      setSortOption((prev) => ({
        ...prev,
        direction: prev.direction === "asc" ? "desc" : "asc",
      }));
    } else {
      // Set new field with default direction
      const defaultDir = SORT_OPTIONS.find((opt) => opt.field === field)?.defaultDir ?? "desc";
      setSortOption({ field, direction: defaultDir });
    }
  };

  return (
    <div className="border-b border-dark-800/50 last:border-b-0">
      {/* Timeframe header */}
      <div className="flex items-center justify-between px-4 py-2.5 pl-10 hover:bg-dark-800/30 transition-colors">
        <button
          onClick={onToggleExpand}
          className="flex items-center gap-2 flex-1"
        >
          <Clock className="h-4 w-4 text-cyan-400" />
          <span className="font-medium text-dark-200">{group.displayTimeframe}</span>
          <span className="text-xs text-dark-500">
            ({group.studyCount} {group.studyCount === 1 ? "version" : "versions"})
          </span>
        </button>
        <div className="flex items-center gap-2">
          {/* Sort dropdown - only show when expanded and has multiple studies */}
          {isExpanded && group.studyCount > 1 && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-1 px-2 py-1 text-xs text-dark-400 hover:text-dark-200
                             bg-dark-800/50 hover:bg-dark-700/50 rounded border border-dark-700 transition-colors"
                  onClick={(e) => e.stopPropagation()}
                >
                  {sortOption.direction === "desc" ? (
                    <ArrowDownAZ className="h-3 w-3" />
                  ) : (
                    <ArrowUpAZ className="h-3 w-3" />
                  )}
                  <span>{currentSortLabel}</span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-40">
                {SORT_OPTIONS.map((opt) => (
                  <DropdownMenuItem
                    key={opt.field}
                    onClick={() => handleSortSelect(opt.field)}
                    className={cn(
                      "flex items-center justify-between",
                      sortOption.field === opt.field && "bg-dark-800"
                    )}
                  >
                    <span>{opt.label}</span>
                    {sortOption.field === opt.field && (
                      <span className="text-xs text-dark-500">
                        {sortOption.direction === "desc" ? "↓" : "↑"}
                      </span>
                    )}
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {group.bestValue !== null && (() => {
            // AIE-319: pull total_trades + toml_config from the study that produced this best value
            const winner = group.studies.find((s) => s.best_value === group.bestValue);
            return (
              <span className="text-xs text-dark-400">
                Best: <span className="text-green-400">{formatStrictWithApprox(
                  group.bestValue,
                  winner?.total_trades,
                  parseApproxConfigFromToml(winner?.toml_config),
                )}</span>
              </span>
            );
          })()}
          <Badge
            variant="outline"
            className={cn("text-xs border", freshnessClass)}
          >
            {group.worstFreshness}
          </Badge>
          <button onClick={onToggleExpand}>
            {isExpanded ? (
              <ChevronDown className="h-4 w-4 text-dark-500" />
            ) : (
              <ChevronRight className="h-4 w-4 text-dark-500" />
            )}
          </button>
        </div>
      </div>

      {/* Study cards */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
          >
            <div className="px-4 pb-3 pl-10 space-y-2">
              {/* Render studies in unified sorted order (parallel groups and standalone intermixed) */}
              {sortedDisplayItems.map((item) => {
                if (item.type === "parallel") {
                  const parallelGroup = item.group;
                  return (
                    <ParallelStudyCard
                      key={parallelGroup.parallel_group_id}
                      parallelGroup={parallelGroup}
                      selectedStudyIds={selectedStudyIdSet}
                      onStudyClick={(study) => onStudyClick(study.study_id)}
                      onStudySelect={(studyId) => {
                        // Find the study in the group and call onStudySelect with it
                        const study = parallelGroup.studies.find((s) => s.study_id === studyId);
                        if (study) {
                          // Need to enrich study with metadata before passing to onStudySelect
                          // For now, we'll create a minimal enriched version
                          onStudySelect(study as StudyWithMetadata);
                        }
                      }}
                      onCompareAll={(studies) => onCompareAll?.(studies)}
                    />
                  );
                } else {
                  const study = item.study;
                  return (
                    <StudyCard
                      key={study.study_id}
                      study={study as StudyWithMetadata}
                      isSelected={selectedStudyIds.includes(study.study_id)}
                      onClick={() => onStudyClick(study.study_id)}
                      onSelect={() => onStudySelect(study as StudyWithMetadata)}
                    />
                  );
                }
              })}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default StudyGroupList;
